import { useState, useEffect, useCallback } from 'react';
import { Loader2, Check, RefreshCw } from 'lucide-react';
import { mediaService } from '../../services/media';
import { FrameInfo } from '../../types';

interface VideoCoverSelectorProps {
  mediaId: string;
  onSelect: (coverUrl: string) => void;
  className?: string;
  disabled?: boolean;
}

export default function VideoCoverSelector({
  mediaId,
  onSelect,
  className = '',
  disabled = false,
}: VideoCoverSelectorProps) {
  const [frames, setFrames] = useState<FrameInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFrames = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await mediaService.getVideoFrames(mediaId);
      setFrames(result.frames);
      if (result.coverIndex != null) {
        setSelectedIndex(result.coverIndex);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load video frames');
    } finally {
      setIsLoading(false);
    }
  }, [mediaId]);

  useEffect(() => {
    loadFrames();
  }, [loadFrames]);

  const handleSelect = useCallback(async (index: number) => {
    if (disabled || isSaving) return;

    setSelectedIndex(index);
    setIsSaving(true);
    setError(null);

    try {
      const result = await mediaService.setVideoCover(mediaId, index);
      onSelect(result.coverUrl);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set video cover');
      setSelectedIndex(null);
    } finally {
      setIsSaving(false);
    }
  }, [mediaId, disabled, isSaving, onSelect]);

  if (isLoading) {
    return (
      <div className={`${className} flex items-center justify-center py-8`}>
        <Loader2 className="w-6 h-6 text-gold animate-spin" />
        <span className="ml-2 text-gray-500">Loading frames...</span>
      </div>
    );
  }

  if (error && frames.length === 0) {
    return (
      <div className={`${className} text-center py-8`}>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadFrames}
          className="flex items-center gap-2 mx-auto px-4 py-2 bg-gray-200 hover:bg-gray-100 rounded-lg text-gray-900 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </button>
      </div>
    );
  }

  if (frames.length === 0) {
    return (
      <div className={`${className} text-center py-8`}>
        <p className="text-gray-500">No frames available. The video may still be processing.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="text-gray-600 text-sm mb-3">Select a cover image for your video:</p>

      <div className="grid grid-cols-5 gap-2">
        {frames.map((frame, index) => (
          <button
            key={frame.filename}
            type="button"
            onClick={() => handleSelect(index)}
            disabled={disabled || isSaving}
            className={`
              relative aspect-video rounded-lg overflow-hidden border-2 transition-all
              ${selectedIndex === index
                ? 'border-gold ring-2 ring-gold/50'
                : 'border-transparent hover:border-gray-300'
              }
              ${disabled || isSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <img
              src={frame.url}
              alt={`Frame ${index + 1}`}
              className="w-full h-full object-cover"
            />
            {selectedIndex === index && (
              <div className="absolute inset-0 bg-gold/20 flex items-center justify-center">
                {isSaving ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Check className="w-5 h-5 text-white" />
                )}
              </div>
            )}
          </button>
        ))}
      </div>

      {error && frames.length > 0 && (
        <p className="mt-2 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}
